import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery, useMutation } from "@tanstack/react-query";
import axios from "axios";
import {
    Search, Loader2, CheckCircle2, AlertCircle,
    Mail, Phone, ListChecks, ShieldCheck,
    ArrowRight, Sun, Moon, Upload, User, BookOpen, Lock,
    CreditCard, FileText, FileBadge, Check, LifeBuoy, Building2, X, Edit2, Mic, MicOff,
    Info,
    AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

// --- Shadcn UI Select Component ---
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

// --- Imported Components & API Client ---
import PaymentGuide from "@/components/public/conference-payment/PaymentGuide";
// ⚠️ SESUAIKAN PATH INI DENGAN LOKASI FILE axiosClient ANDA ⚠️
import { axiosClient } from "@/lib/axiosClient";

// ==========================================
// TYPES & INTERFACES
// ==========================================

interface SubmissionData {
    id: string;
    conferenceName: string;
    paperTitle: string;
    authors: string;
}

type RoleType = "presenter" | "non_presenter";
type ParticipantType = "general" | "student_s1_s2" | "student_s3";

interface PaymentGateway {
    id: number;
    nama_payment: string;
    prefix_va: string;
    kode_payment: string;
    kode_bank: string;
    vendor: string;
    beta: number;
}

interface Conference {
    conference_id: number;
    name: string;
    slug: string;
}

interface CheckSubmissionResponse {
    status: string;
    data: {
        ojs_submission_id: number;
        title: string;
        authors: string;
        submission_status: string;
        current_stage: string;
        conference_name: string;
    };
}

interface StudentData {
    nim: string;
    nama_lengkap: string;
    nik: string;
    email: string;
    nama_jenjang: string;
    [key: string]: unknown;
}

interface StudentReferenceResponse {
    status: string;
    message: string;
    data: StudentData;
}

interface CheckPriceResponse {
    status: string;
    message: string;
    data: {
        package_id: number;
        package_name: string;
        tier_name: string;
        currency: string;
        base_price: string;
        quantity: string;
        total_amount: number;
    };
}

interface StoreRegistrationResponse {
    status: string;
    message: string;
    data: {
        invoice_number: string;
        payment_token: string;
        amount: string;
        quantity: number;
        verification_status: string;
        requires_payment_now: boolean;
        email_sent: boolean;
    };
}

interface LaravelErrorResponse {
    message: string;
    errors?: Record<string, string[]>;
}

// --- Helper: Parse Laravel Validation Errors ---
const parseLaravelError = (error: unknown): string => {
    if (axios.isAxiosError<LaravelErrorResponse>(error)) {
        const data = error.response?.data;
        if (data?.errors) {
            const firstErrorKey = Object.keys(data.errors)[0];
            return data.errors[firstErrorKey][0];
        }
        return data?.message || "Terjadi kesalahan sistem. Silakan coba lagi.";
    }
    if (error instanceof Error) return error.message;
    return "Terjadi kesalahan yang tidak diketahui.";
};

// ==========================================
// REUSABLE COMPONENTS
// ==========================================

interface PaymentCardProps {
    id: string;
    name: string;
    description: string;
    logoUrl: string;
    badgeText: string;
    badgeColor?: "orange" | "green" | "blue";
    selected: boolean;
    onSelect: (id: string) => void;
    disabled?: boolean;
}

function PaymentMethodCard({
    id, name, description, logoUrl, badgeText, badgeColor = "blue",
    selected, onSelect, disabled = false
}: PaymentCardProps) {
    const badgeStyles = {
        orange: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400",
        green: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400",
        blue: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400",
    };

    return (
        <motion.div
            whileHover={!disabled ? { y: -2 } : {}}
            whileTap={!disabled ? { scale: 0.98 } : {}}
            className={`relative flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-300 ease-in-out
                ${disabled ? "opacity-50 grayscale cursor-not-allowed bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800" : ""}
                ${selected && !disabled
                    ? "border-blue-600 bg-blue-50/40 dark:bg-blue-900/20 shadow-md ring-2 ring-blue-600/20 dark:ring-blue-500/20"
                    : !disabled ? "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 bg-white dark:bg-slate-800/80" : ""
                }`}
            onClick={() => !disabled && onSelect(id)}
        >
            <div className="shrink-0 w-14 flex items-center justify-center bg-white p-1 rounded-md border border-slate-100 dark:border-slate-200/20">
                <img src={logoUrl} alt={name} className="w-full h-auto object-contain" />
            </div>
            <div className="w-[1.5px] h-10 bg-slate-200 dark:bg-slate-700 mx-4 shrink-0 rounded-full"></div>
            <div className="flex flex-col flex-grow pr-8">
                <div className="flex items-center gap-2 mb-0.5">
                    <span className={`font-bold text-sm transition-colors ${selected ? "text-blue-900 dark:text-blue-100" : "text-slate-900 dark:text-slate-100"}`}>
                        {name}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${badgeStyles[badgeColor]}`}>
                        {badgeText}
                    </span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-snug">
                    {description}
                </p>
            </div>
            <div className="absolute right-4 flex items-center justify-center">
                {selected ? (
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 300, damping: 20 }}>
                        <CheckCircle2 className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="currentColor" stroke="white" />
                    </motion.div>
                ) : disabled ? (
                    <Lock className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                ) : null}
            </div>
        </motion.div>
    );
}

// ==========================================
// MAIN PAGE COMPONENT
// ==========================================

export default function ConferencePaymentPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // --- Globals & Toast State ---
    const [isDark, setIsDark] = useState<boolean>(false);
    const [toast, setToast] = useState<{ message: string; type: "error" | "success" } | null>(null);

    const showToast = (message: string, type: "error" | "success" = "error") => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 5000);
    };

    // --- Role & Validation States ---
    const [role, setRole] = useState<RoleType>("presenter");
    const [submissionId, setSubmissionId] = useState<string>("");
    const [conferenceSlug, setConferenceSlug] = useState<string>("");
    const [isVerified, setIsVerified] = useState<boolean>(false);
    const [submissionData, setSubmissionData] = useState<SubmissionData | null>(null);

    // --- Step Form Data ---
    const [participantName, setParticipantName] = useState<string>("");
    const [institutionName, setInstitutionName] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [whatsapp, setWhatsapp] = useState<string>("");

    // Category
    const [participantType, setParticipantType] = useState<ParticipantType>("general");
    const [isInternalStudent, setIsInternalStudent] = useState<boolean>(false);
    const [nim, setNim] = useState<string>("");
    const [nik, setNik] = useState<string>("");
    const [ktmFile, setKtmFile] = useState<File | null>(null);

    const [isInternalVerified, setIsInternalVerified] = useState<boolean>(false);
    const [internalStudentData, setInternalStudentData] = useState<StudentData | null>(null);

    // --- Payment Method ---
    const [selectedMethod, setSelectedMethod] = useState<string>("");

    // ==========================================
    // INITIALIZATION & SINKRONISASI TEMA
    // ==========================================

    useEffect(() => {
        const savedTheme = localStorage.getItem("theme");
        if (savedTheme === "dark") {
            setIsDark(true);
            document.documentElement.classList.add("dark");
        } else {
            setIsDark(false);
            document.documentElement.classList.remove("dark");
        }
    }, []);

    const toggleDarkMode = () => {
        const newMode = !isDark;
        setIsDark(newMode);
        if (newMode) {
            document.documentElement.classList.add("dark");
            localStorage.setItem("theme", "dark");
        } else {
            document.documentElement.classList.remove("dark");
            localStorage.setItem("theme", "light");
        }
    };

    // ==========================================
    // REACT QUERY & API MUTATIONS
    // ==========================================

    const { data: conferences, isLoading: isLoadingConferences } = useQuery({
        queryKey: ["conferences"],
        queryFn: async () => {
            const response = await axiosClient.get("/conference-billing/conferences");
            return response.data.data as Conference[];
        },
        staleTime: 1000 * 60 * 60,
    });

    const { data: gateways, isLoading: isLoadingGateways } = useQuery({
        queryKey: ["payment-gateways"],
        queryFn: async () => {
            const response = await axiosClient.get("/conference-billing/reference/payment-gateway");
            return response.data.data as PaymentGateway[];
        },
        staleTime: 1000 * 60 * 60,
    });

    const checkSubmissionMutation = useMutation({
        mutationFn: async (payload: { slug: string; ojs_submission_id: string }) => {
            const response = await axiosClient.post<CheckSubmissionResponse>("/conference-billing/check-submission", payload);
            return response.data.data;
        },
        onSuccess: (data) => {
            setSubmissionData({
                id: data.ojs_submission_id.toString(),
                conferenceName: data.conference_name,
                paperTitle: data.title,
                authors: data.authors,
            });
            setIsVerified(true);
            showToast("Data jurnal berhasil ditemukan!", "success");
        },
        onError: (error: unknown) => {
            setIsVerified(false);
            setSubmissionData(null);
            showToast(parseLaravelError(error));
        }
    });

    useEffect(() => {
        const pRole = searchParams.get("presenter");
        const pTrack = searchParams.get("conference-track");
        const pSid = searchParams.get("submission-id");

        if (pRole === "0") {
            setRole("non_presenter");
            if (pTrack) setConferenceSlug(pTrack);
        } else {
            setRole("presenter");
            if (pTrack) setConferenceSlug(pTrack);
            if (pSid) setSubmissionId(pSid);

            if (pRole === "1" && pTrack && pSid) {
                setTimeout(() => {
                    checkSubmissionMutation.mutate({ slug: pTrack, ojs_submission_id: pSid });
                }, 500);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkStudentMutation = useMutation({
        mutationFn: async (payload: { nim: string; nik: string; participant_type: string }) => {
            const response = await axiosClient.post<StudentReferenceResponse>("/conference-billing/reference/student", payload);
            return response.data.data;
        },
        onSuccess: (data) => {
            setIsInternalVerified(true);
            setInternalStudentData(data);

            if (!participantName) setParticipantName(data.nama_lengkap);
            if (!email) setEmail(data.email);
            if (!institutionName) setInstitutionName("Universitas Pendidikan Ganesha");

            showToast("Verifikasi mahasiswa berhasil!", "success");
        },
        onError: (error: unknown) => {
            setIsInternalVerified(false);
            setInternalStudentData(null);
            showToast(parseLaravelError(error));
        }
    });

    const selectedGw = gateways?.find(g => g.id.toString() === selectedMethod);
    const activeCurrency = selectedGw ? (selectedGw.nama_payment.includes('USD') ? 'USD' : 'IDR') : "";
    const isPresenterInt = role === "presenter" ? 1 : 0;

    const isBaseVerified = role === "presenter" ? isVerified : (conferenceSlug !== "");
    const isReadyForPrice = isBaseVerified && activeCurrency !== "";

    const { data: priceData, isFetching: isFetchingPrice, error: priceError } = useQuery({
        queryKey: ["checkPrice", isPresenterInt, participantType, activeCurrency, 1],
        queryFn: async () => {
            const payload = {
                is_presenter: isPresenterInt,
                participant_type: participantType,
                currency: activeCurrency,
                quantity: 1
            };
            const response = await axiosClient.post<CheckPriceResponse>("/conference-billing/check-price", payload);
            return response.data.data;
        },
        enabled: isReadyForPrice,
        retry: 0
    });

    useEffect(() => {
        if (priceError) {
            showToast(parseLaravelError(priceError));
        }
    }, [priceError]);

    const storeRegistrationMutation = useMutation({
        mutationFn: async (formData: FormData) => {
            const response = await axiosClient.post<StoreRegistrationResponse>("/conference-billing/store", formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        },
        onSuccess: (data) => {
            showToast(data.message || "Pendaftaran berhasil disimpan!", "success");

            // ✅ SEMUA STATUS (Pending/Approved) DIARAHKAN KE SATU URL
            const safeToken = encodeURIComponent(data.data.payment_token);
            // ✅ TAMBAHKAN STATE { emailSent } SAAT REDIRECT
            navigate(`/conference-payment/invoice/${safeToken}`, {
                state: { emailSent: data.data.email_sent }
            });
        },
        onError: (error: unknown) => {
            showToast(parseLaravelError(error));
        }
    });

    // ==========================================
    // HANDLERS
    // ==========================================

    const handleRoleChange = (newRole: RoleType) => {
        setRole(newRole);
        if (newRole === "presenter") {
            setIsVerified(false);
            setSubmissionData(null);
        }
    };

    const handleCheckSubmission = (): void => {
        const cleanId = submissionId.trim();
        if (!cleanId || !conferenceSlug) return;
        checkSubmissionMutation.mutate({ slug: conferenceSlug, ojs_submission_id: cleanId });
    };

    const handleVerifyInternalStudent = (): void => {
        if (!nim.trim() || !nik.trim()) {
            showToast("Harap masukkan NIM dan NIK dengan benar.");
            return;
        }
        checkStudentMutation.mutate({
            nim: nim.trim(),
            nik: nik.trim(),
            participant_type: participantType
        });
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setKtmFile(e.target.files[0]);
        }
    };

    const handleParticipantTypeChange = (type: ParticipantType) => {
        setParticipantType(type);
        setIsInternalVerified(false);

        if (type === "general") {
            setIsInternalStudent(false);
            setNim("");
            setNik("");
            setKtmFile(null);
        }
    };

    const handleInternalChange = (isInternal: boolean) => {
        setIsInternalStudent(isInternal);
        setIsInternalVerified(false);
        setNim("");
        setNik("");
        setKtmFile(null);
    };

    const handleSubmitData = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();

        if (role === "presenter" && (!isVerified || !submissionData)) return;
        if (role === "non_presenter" && !conferenceSlug) {
            showToast("Harap pilih Track Konferensi yang diikuti.");
            return;
        }
        if (!selectedMethod || !priceData) return;

        const isStudent = participantType !== 'general';
        const needsKtm = isStudent && !isInternalStudent;

        if (needsKtm && !ktmFile) {
            showToast("Harap unggah Kartu Tanda Mahasiswa (KTM).");
            return;
        }

        if (isStudent && isInternalStudent && !isInternalVerified) {
            showToast("Harap verifikasi NIM dan NIK Anda terlebih dahulu.");
            return;
        }

        const selectedConferenceData = conferences?.find(j => j.slug === conferenceSlug);
        const finalConferenceId = selectedConferenceData ? selectedConferenceData.conference_id.toString() : "";

        if (!finalConferenceId) {
            showToast("Data jurnal/track tidak valid. Silakan muat ulang halaman.");
            return;
        }

        const formData = new FormData();

        formData.append("conference_id", finalConferenceId);
        formData.append("is_presenter", isPresenterInt.toString());
        formData.append("participant_name", participantName);
        formData.append("institution_name", institutionName);
        formData.append("email", email);
        formData.append("whatsapp_number", whatsapp);
        formData.append("currency", activeCurrency);
        formData.append("participant_type", participantType);
        formData.append("is_internal_student", isInternalStudent ? "1" : "0");
        formData.append("quantity", "1");
        formData.append("gateway_transaction_id", selectedMethod);

        if (role === "presenter" && submissionData) {
            formData.append("ojs_submission_id", submissionData.id);
            formData.append("submission_title", submissionData.paperTitle);
            formData.append("submission_authors", submissionData.authors);
            formData.append("presenter_names", participantName);
        }

        if (isStudent) {
            if (isInternalStudent) {
                formData.append("nim", nim);
                formData.append("nik", nik);
            } else if (ktmFile) {
                formData.append("ktm_file", ktmFile);
            }
        }

        storeRegistrationMutation.mutate(formData);
    };

    const formatCurrency = (amount: number, currencyCode: string) => {
        return new Intl.NumberFormat(currencyCode === 'USD' ? 'en-US' : 'id-ID', {
            style: 'currency', currency: currencyCode, minimumFractionDigits: 0
        }).format(amount);
    };

    const isStudent = participantType !== 'general';
    const needsVerification = isStudent && !isInternalStudent;

    const isReadyToSubmit = isBaseVerified && selectedMethod !== "" && priceData && !priceError &&
        (!isStudent || (isInternalStudent ? isInternalVerified : ktmFile !== null)) &&
        participantName && institutionName && email && whatsapp;

    // ==========================================
    // RENDER COMPONENTS
    // ==========================================

    const ActionButton = () => (
        <Button
            type="submit"
            form="payment-form"
            disabled={!isReadyToSubmit || storeRegistrationMutation.isPending || isFetchingPrice}
            className={`w-full h-14 md:h-16 text-sm md:text-lg font-black text-white shadow-xl rounded-xl md:rounded-2xl transition-all group hover:-translate-y-1 active:scale-[0.98] flex items-center justify-center gap-2 md:gap-3
                ${needsVerification
                    ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-orange-500/20'
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-blue-600/20'
                }`}
        >
            {storeRegistrationMutation.isPending ? (
                <><Loader2 className="w-5 h-5 md:w-6 md:h-6 animate-spin" /><span>Processing...</span></>
            ) : needsVerification ? (
                <>SUBMIT FOR VERIFICATION <ArrowRight className="w-5 h-5 md:w-6 md:h-6 group-hover:translate-x-1.5 transition-transform" /></>
            ) : (
                <>GENERATE INVOICE <ArrowRight className="w-5 h-5 md:w-6 md:h-6 group-hover:translate-x-1.5 transition-transform" /></>
            )}
        </Button>
    );

    const SummaryCard = ({ isDesktop }: { isDesktop: boolean }) => (
        <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-xl md:shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 dark:from-slate-950 dark:to-slate-900 p-6 flex justify-between items-center relative overflow-hidden">
                <div className="absolute -right-4 -top-4 w-16 h-16 bg-white/5 rounded-full blur-xl"></div>
                <h3 className="font-bold text-xs uppercase tracking-widest text-white opacity-90 flex items-center gap-2 relative z-10">
                    <FileText className="w-4 h-4" /> Billing Summary
                </h3>
            </div>

            <div className="p-6 md:p-8">
                {isBaseVerified ? (
                    <div className="space-y-5">
                        <div className="flex justify-between text-sm items-center">
                            <span className="text-slate-500 dark:text-slate-400 font-medium">Role</span>
                            <span className="font-bold text-blue-700 dark:text-blue-400 uppercase text-[11px] px-2.5 py-1 bg-blue-50 dark:bg-blue-900/40 rounded-md">
                                {role === "presenter" ? "Presenter" : "Non-Presenter"}
                            </span>
                        </div>
                        <div className="flex justify-between text-sm items-center">
                            <span className="text-slate-500 dark:text-slate-400 font-medium">Category</span>
                            <span className="font-bold text-slate-800 dark:text-slate-200 uppercase text-[11px] px-2.5 py-1 bg-slate-100 dark:bg-slate-800 rounded-md">
                                {participantType === 'general' ? 'General' : (participantType === 'student_s1_s2' ? 'Student (D4/S1/S2)' : 'Student (S3)')}
                            </span>
                        </div>
                        <div className="flex justify-between text-sm items-center border-b pb-6 border-slate-100 dark:border-slate-800">
                            <span className="text-slate-500 dark:text-slate-400 font-medium">Process Flow</span>
                            <span className={`font-bold uppercase text-[10px] px-2.5 py-1 rounded-md tracking-wider ${needsVerification ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400' : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'}`}>
                                {needsVerification ? 'Pending Review' : 'Direct VA'}
                            </span>
                        </div>

                        <div className="flex flex-col pt-2 border-t border-slate-100 dark:border-slate-800">
                            <span className="text-slate-500 dark:text-slate-400 text-xs font-bold uppercase tracking-wider mb-1.5">Estimated Total</span>

                            <div className="min-h-[40px] flex items-center">
                                {isFetchingPrice ? (
                                    <div className="flex items-center gap-3">
                                        <Loader2 className="w-6 h-6 animate-spin text-blue-600 dark:text-blue-400" />
                                        <span className="text-sm font-medium text-slate-500 animate-pulse">Calculating...</span>
                                    </div>
                                ) : priceError ? (
                                    <span className="text-sm font-semibold text-red-500 flex items-center gap-1">
                                        <AlertCircle className="w-4 h-4" /> Package not available
                                    </span>
                                ) : priceData ? (
                                    <div className="flex flex-col">
                                        <span className="text-4xl font-black text-slate-900 dark:text-white tracking-tighter">
                                            {formatCurrency(priceData.total_amount, priceData.currency)}
                                        </span>
                                        <span className="text-xs text-blue-600 dark:text-blue-400 font-bold mt-1">
                                            {priceData.package_name} ({priceData.tier_name.replace('_', ' ')})
                                        </span>
                                    </div>
                                ) : (
                                    <span className="text-lg font-bold text-slate-300 dark:text-slate-700">Select payment method</span>
                                )}
                            </div>
                            <span className="text-[11px] text-slate-400 mt-2 italic">Excluding bank administration fees</span>
                        </div>

                        {isDesktop && (
                            <div className="pt-5 mt-3 border-t border-slate-100 dark:border-slate-800">
                                <ActionButton />
                                <p className="text-center text-[11px] text-slate-400 mt-4 flex items-center justify-center gap-1.5">
                                    <ShieldCheck className="w-3.5 h-3.5" /> Secure and encrypted transaction
                                </p>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="py-4 text-center flex flex-col items-center justify-center opacity-50 grayscale">
                        <CreditCard className="w-12 h-32 text-slate-300 dark:text-slate-600 mb-3" />
                        <p className="text-sm font-medium text-slate-500">
                            {role === "presenter" ? "Validate your Submission ID first." : "Select your conference track first."}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#FAF9F6] dark:bg-slate-950 flex flex-col relative font-sans transition-colors duration-300">
            <div className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'url(/ornamen/songket-pattern.png)', backgroundSize: '400px' }}></div>

            {/* --- CUSTOM TOAST NOTIFICATION --- */}
            <AnimatePresence>
                {toast && (
                    <motion.div
                        initial={{ opacity: 0, y: -50, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20, scale: 0.9 }}
                        className="fixed top-24 left-1/2 -translate-x-1/2 z-50 px-4 w-full max-w-md"
                    >
                        <div className={`flex items-start gap-3 p-4 rounded-xl shadow-2xl border ${toast.type === 'error' ? 'bg-white border-red-200 text-red-700 dark:bg-slate-900 dark:border-red-900/50 dark:text-red-400' : 'bg-white border-green-200 text-green-700 dark:bg-slate-900 dark:border-green-900/50 dark:text-green-400'}`}>
                            {toast.type === 'error' ? <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" /> : <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />}
                            <p className="text-sm font-medium leading-snug flex-1">{toast.message}</p>
                            <button type="button" onClick={() => setToast(null)} className="opacity-50 hover:opacity-100 transition-opacity">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* --- PREMIUM HEADER --- */}
            <header className="sticky top-0 z-40 w-full bg-[#0B1120] dark:bg-slate-950 border-b border-white/10 dark:border-slate-800 shadow-xl overflow-hidden transition-colors duration-300">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl pointer-events-none -translate-y-1/2"></div>
                <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-600/10 rounded-full blur-3xl pointer-events-none translate-y-1/2"></div>

                <div className="container mx-auto px-4 py-3 md:py-4 max-w-7xl flex items-center justify-between relative z-10">
                    <div className="flex items-center gap-4">
                        <div className="bg-white p-2 rounded-xl shadow-[0_0_15px_rgba(255,255,255,0.1)] shrink-0 flex items-center justify-center">
                            <img src="/konaspi.png" alt="Konaspi Logo" className="h-8 md:h-10 w-auto" />
                        </div>
                        <div className="flex flex-col">
                            <h1 className="text-xl md:text-2xl font-extrabold text-white tracking-tight">
                                Konaspi XII <span className="text-blue-400">Payment Portal</span>
                            </h1>
                            <p className="text-slate-400 text-xs md:text-sm hidden sm:block mt-0.5 font-medium">
                                Registration and Billing Form
                            </p>
                        </div>
                    </div>

                    <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="rounded-full w-10 h-10 bg-white/5 hover:bg-white/15 border border-white/10 shrink-0 text-white">
                        {isDark ? <Sun className="h-4 w-4 text-yellow-400" /> : <Moon className="h-4 w-4" />}
                    </Button>
                </div>
            </header>

            <section className="container mx-auto px-4 max-w-7xl pt-8 pb-32 md:pb-20 relative z-20 flex-grow">
                <div className="flex flex-col lg:grid lg:grid-cols-12 gap-8 items-start">

                    <div className="w-full lg:col-span-8 space-y-6">

                        {/* --- ROLE TOGGLE KANAN --- */}
                        <div className="flex justify-end mb-4">
                            <div className="bg-white dark:bg-slate-900 p-1.5 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 inline-flex">
                                <button type="button" onClick={() => handleRoleChange("non_presenter")} className={`px-6 py-3 text-sm md:text-base font-bold rounded-xl transition-all flex items-center gap-2 ${role === "non_presenter" ? "bg-blue-600 text-white shadow-md" : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"}`}>
                                    <MicOff className="w-4 h-4" /> As Non-Presenter
                                </button>
                                <button type="button" onClick={() => handleRoleChange("presenter")} className={`px-6 py-3 text-sm md:text-base font-bold rounded-xl transition-all flex items-center gap-2 ${role === "presenter" ? "bg-blue-600 text-white shadow-md" : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"}`}>
                                    <Mic className="w-4 h-4" /> As Presenter
                                </button>
                            </div>
                        </div>

                        <form id="payment-form" onSubmit={handleSubmitData} className="space-y-6 mb-0">

                            {/* STEP 1 (ONLY FOR PRESENTER) */}
                            <AnimatePresence mode="wait">
                                {role === "presenter" && (
                                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="bg-white dark:bg-slate-900 rounded-3xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col">
                                        <div className="p-6 md:p-8 border-b border-slate-100 dark:border-slate-800">
                                            <div className="flex items-center gap-4">
                                                <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 dark:bg-blue-500/10 font-black text-xl shrink-0">1</div>
                                                <div>
                                                    <h3 className="text-xl font-extrabold text-slate-900 dark:text-white">Conference Validation</h3>
                                                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Automatically pull your scientific paper data</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="p-6 md:p-8 bg-slate-50/30 dark:bg-slate-900/50">
                                            <div className="flex flex-col md:flex-row items-center p-1.5 bg-white dark:bg-slate-800/80 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm focus-within:ring-2 focus-within:ring-blue-500/20">
                                                <div className="flex items-center w-full md:w-[30%] px-4 py-2 shrink-0">
                                                    <Search className="w-5 h-5 text-slate-400 shrink-0" />
                                                    <input type="text" placeholder="Submission ID..." className="w-full bg-transparent border-none focus:outline-none text-slate-700 dark:text-slate-200 ml-3" value={submissionId} onChange={(e) => { setSubmissionId(e.target.value); setIsVerified(false); }} disabled={checkSubmissionMutation.isPending || storeRegistrationMutation.isPending} />
                                                </div>
                                                <div className="hidden md:block w-px h-8 bg-slate-200 dark:bg-slate-700 mx-1 shrink-0"></div>

                                                <div className="w-full md:flex-1 min-w-0 px-4 py-3 md:py-0 border-t md:border-t-0 border-slate-100 dark:border-slate-700 flex items-center">
                                                    {isLoadingConferences ? (
                                                        <div className="w-full flex items-center justify-between text-slate-400 text-sm py-2">
                                                            <span>Loading Tracks...</span>
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        </div>
                                                    ) : (
                                                        <Select
                                                            key={`conference-select-${conferences?.length || 0}`}
                                                            value={conferenceSlug || undefined}
                                                            onValueChange={(val) => { setConferenceSlug(val); setIsVerified(false); }}
                                                            disabled={checkSubmissionMutation.isPending || storeRegistrationMutation.isPending}
                                                        >
                                                            <SelectTrigger className="w-full h-auto py-2 bg-transparent border-none focus:ring-0 focus:ring-offset-0 text-slate-700 dark:text-slate-200 font-medium text-sm md:text-base shadow-none [&>span]:truncate">
                                                                <SelectValue placeholder="Conference Type..." />
                                                            </SelectTrigger>
                                                            <SelectContent className="rounded-xl border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl max-h-[300px] w-[var(--radix-select-trigger-width)] md:w-[400px]">
                                                                {conferences?.map((conference) => (
                                                                    <SelectItem key={conference.conference_id} value={conference.slug} className="cursor-pointer focus:bg-blue-50 dark:focus:bg-slate-700 dark:text-slate-200 py-2.5">
                                                                        {conference.slug.toUpperCase()} - {conference.name}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    )}
                                                </div>

                                                <Button type="button" onClick={handleCheckSubmission} disabled={checkSubmissionMutation.isPending || !submissionId.trim() || !conferenceSlug} className="w-full md:w-auto h-12 px-6 rounded-xl bg-slate-900 hover:bg-slate-800 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-bold mt-2 md:mt-0 shrink-0">
                                                    {checkSubmissionMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Checking...</> : "Verify Data"}
                                                </Button>
                                            </div>

                                            <AnimatePresence>
                                                {isVerified && submissionData && !checkSubmissionMutation.isPending && (
                                                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 p-6 rounded-2xl border border-blue-100 bg-blue-50/60 dark:bg-blue-900/10 dark:border-blue-900/40">
                                                        <div className="flex gap-4">
                                                            <div className="p-3.5 bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded-xl shrink-0 h-fit"><BookOpen className="w-6 h-6" /></div>
                                                            <div className="space-y-3 w-full">
                                                                <div>
                                                                    <p className="text-[10px] font-extrabold text-blue-600 dark:text-blue-400 uppercase mb-1.5">Found: {submissionData.conferenceName}</p>
                                                                    <h4 className="font-bold text-slate-900 dark:text-slate-100 text-lg leading-tight">{submissionData.paperTitle}</h4>
                                                                </div>
                                                                <div className="pt-3 border-t border-blue-100 dark:border-blue-800/50 flex items-center gap-2">
                                                                    <User className="w-4 h-4 text-slate-400" />
                                                                    <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{submissionData.authors}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* STEP 2 (DATA DIRI) */}
                            {(role === "non_presenter" || isVerified) && (
                                <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col animate-in slide-in-from-bottom-4">
                                    <div className="p-6 md:p-8 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900">
                                        <div className="flex items-center gap-4">
                                            <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400 font-black text-xl shrink-0">
                                                {role === "presenter" ? "2" : "1"}
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-extrabold text-slate-900 dark:text-white">
                                                    {role === "presenter" ? "Presenter Information" : "Participant Information"}
                                                </h3>
                                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Complete your data for certificate & billing purposes</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-6 md:p-8 space-y-8 bg-slate-50/30 dark:bg-slate-900/50">

                                        {/* Untuk Non Presenter: Wajib pilih Track Konferensi di awal */}
                                        {role === "non_presenter" && (
                                            <div className="space-y-2 border-b border-slate-200 dark:border-slate-800 pb-8">
                                                <Label className="font-bold text-slate-700 dark:text-slate-300">Conference Track (Conference)</Label>
                                                {isLoadingConferences ? (
                                                    <div className="flex h-12 w-full items-center justify-between rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50 px-4 text-sm text-slate-400">
                                                        <span>Loading Tracks...</span>
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    </div>
                                                ) : (
                                                    <Select
                                                        key={`track-select-${conferences?.length || 0}`}
                                                        value={conferenceSlug || undefined}
                                                        onValueChange={setConferenceSlug}
                                                    >
                                                        <SelectTrigger className="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm focus:ring-2 focus:ring-blue-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 [&>span]:truncate">
                                                            <SelectValue placeholder="Select the conference track..." />
                                                        </SelectTrigger>
                                                        <SelectContent className="rounded-xl border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl max-h-60">
                                                            {conferences?.map((conference) => (
                                                                <SelectItem key={conference.conference_id} value={conference.slug} className="cursor-pointer focus:bg-blue-50 dark:focus:bg-slate-700 dark:text-slate-200 py-2.5">
                                                                    {conference.slug.toUpperCase()} - {conference.name}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                )}
                                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">Select which conference track you are attending.</p>
                                            </div>
                                        )}

                                        <div className="space-y-6">
                                            <div>
                                                <h4 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-4">Registration Category</h4>
                                                <RadioGroup onValueChange={(v) => handleParticipantTypeChange(v as ParticipantType)} value={participantType} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                                    <Label htmlFor="type_general" className={`flex flex-col p-4 border-2 rounded-xl cursor-pointer transition-all ${participantType === 'general' ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400' : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 bg-white dark:bg-slate-800/50'}`}>
                                                        <RadioGroupItem value="general" id="type_general" className="sr-only" />
                                                        <span className="font-bold text-sm">General</span>
                                                        <span className="text-xs text-slate-500 dark:text-slate-400 mt-1">Standard rate</span>
                                                    </Label>
                                                    <Label htmlFor="type_s1_s2" className={`flex flex-col p-4 border-2 rounded-xl cursor-pointer transition-all ${participantType === 'student_s1_s2' ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400' : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 bg-white dark:bg-slate-800/50'}`}>
                                                        <RadioGroupItem value="student_s1_s2" id="type_s1_s2" className="sr-only" />
                                                        <span className="font-bold text-sm">Student (D4/S1/S2)</span>
                                                        <span className="text-xs text-slate-500 dark:text-slate-400 mt-1">Bachelor/Master</span>
                                                    </Label>
                                                    <Label htmlFor="type_s3" className={`flex flex-col p-4 border-2 rounded-xl cursor-pointer transition-all ${participantType === 'student_s3' ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400' : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 bg-white dark:bg-slate-800/50'}`}>
                                                        <RadioGroupItem value="student_s3" id="type_s3" className="sr-only" />
                                                        <span className="font-bold text-sm">Student (S3)</span>
                                                        <span className="text-xs text-slate-500 dark:text-slate-400 mt-1">Doctoral</span>
                                                    </Label>
                                                </RadioGroup>
                                            </div>

                                            <AnimatePresence mode="wait">
                                                {isStudent && (
                                                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, height: 0 }} className="p-5 bg-blue-50/50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-900/30 space-y-5">
                                                        <div className="flex items-center justify-between flex-wrap gap-3">
                                                            <Label className="font-bold text-slate-800 dark:text-slate-200">Are you currently studying at Undiksha?</Label>
                                                            <div className="flex bg-white dark:bg-slate-800 p-1 rounded-lg border border-slate-200 dark:border-slate-700">
                                                                <button type="button" onClick={() => handleInternalChange(true)} className={`px-4 py-1.5 text-sm font-bold rounded-md transition-all ${isInternalStudent ? "bg-blue-600 text-white shadow-sm" : "text-slate-500 hover:text-slate-700 dark:text-slate-400"}`}>Yes, I am</button>
                                                                <button type="button" onClick={() => handleInternalChange(false)} className={`px-4 py-1.5 text-sm font-bold rounded-md transition-all ${!isInternalStudent ? "bg-blue-600 text-white shadow-sm" : "text-slate-500 hover:text-slate-700 dark:text-slate-400"}`}>No, I'm Not</button>
                                                            </div>
                                                        </div>

                                                        {isInternalStudent && (
                                                            <div className="pt-3 border-t border-blue-200 dark:border-blue-900/50">
                                                                {!isInternalVerified ? (
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                        <div className="space-y-2">
                                                                            <Label className="font-bold text-slate-700 dark:text-slate-300">Student ID (NIM)</Label>
                                                                            <Input placeholder="e.g., 1515051..." className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white" value={nim} onChange={e => { setNim(e.target.value); setIsInternalVerified(false); }} disabled={checkStudentMutation.isPending} required />
                                                                        </div>
                                                                        <div className="space-y-2">
                                                                            <Label className="font-bold text-slate-700 dark:text-slate-300">National ID (NIK)</Label>
                                                                            <Input placeholder="16 digit NIK" maxLength={16} className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white" value={nik} onChange={e => { setNik(e.target.value); setIsInternalVerified(false); }} disabled={checkStudentMutation.isPending} required />
                                                                        </div>
                                                                        <div className="md:col-span-2 flex items-center justify-end mt-2">
                                                                            <Button type="button" className="h-11 px-6 w-full sm:w-auto font-bold shrink-0 bg-blue-600 hover:bg-blue-700 text-white" onClick={handleVerifyInternalStudent} disabled={!nim || !nik || checkStudentMutation.isPending}>
                                                                                {checkStudentMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                                                                Verify NIM & NIK
                                                                            </Button>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex flex-col sm:flex-row justify-between gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
                                                                        <div className="flex items-center gap-3 text-green-700 dark:text-green-400">
                                                                            <CheckCircle2 className="w-6 h-6 shrink-0" />
                                                                            <div>
                                                                                <span className="text-sm font-bold block">Verified Student</span>
                                                                                <span className="text-xs">{internalStudentData?.nama_lengkap} ({internalStudentData?.nim}) &bull; Jenjang: {internalStudentData?.nama_jenjang}</span>
                                                                            </div>
                                                                        </div>
                                                                        <Button type="button" variant="outline" size="sm" onClick={() => setIsInternalVerified(false)} className="shrink-0 border-green-300 text-green-700 hover:bg-green-100 dark:border-green-800 dark:text-green-400 dark:hover:bg-green-900/40">
                                                                            <Edit2 className="w-4 h-4 mr-2" /> Change Data
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}

                                                        {!isInternalStudent && (
                                                            <div className="pt-3 border-t border-blue-200 dark:border-blue-900/50">
                                                                <Label className="font-bold text-orange-800 dark:text-orange-400 flex items-center gap-2 mb-3">
                                                                    <FileBadge className="w-4 h-4" /> Upload Student ID (KTM) - Required
                                                                </Label>
                                                                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-orange-300 border-dashed rounded-xl cursor-pointer bg-white dark:bg-slate-900 hover:bg-orange-50 dark:hover:bg-slate-800 transition-colors">
                                                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                                        {ktmFile ? (
                                                                            <><Check className="w-6 h-6 mb-1 text-green-500" /><p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{ktmFile.name}</p></>
                                                                        ) : (
                                                                            <><Upload className="w-6 h-6 mb-2 text-orange-500" /><p className="text-sm text-slate-600 dark:text-slate-400"><span className="font-semibold text-orange-600 dark:text-orange-400">Click to upload</span> or drag & drop</p></>
                                                                        )}
                                                                    </div>
                                                                    <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} required />
                                                                </label>
                                                            </div>
                                                        )}
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-slate-200 dark:border-slate-800 pt-8">
                                            <div className="space-y-2">
                                                <Label className="font-bold text-slate-700 dark:text-slate-300">
                                                    {role === "presenter" ? "Presenter Full Name" : "Buyer / Full Name"}
                                                </Label>
                                                <div className="relative">
                                                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                                    <Input
                                                        placeholder={role === "presenter" ? "Full name of the presenter" : "Full name of participant/buyer"}
                                                        className="pl-11 h-12 rounded-xl bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100"
                                                        value={participantName}
                                                        onChange={e => setParticipantName(e.target.value)}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="font-bold text-slate-700 dark:text-slate-300">Institution / University</Label>
                                                <div className="relative">
                                                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                                    <Input placeholder="e.g. Universitas Pendidikan Ganesha" className="pl-11 h-12 rounded-xl bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100" value={institutionName} onChange={e => setInstitutionName(e.target.value)} required />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="font-bold text-slate-700 dark:text-slate-300">Primary Email</Label>
                                                <div className="relative">
                                                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                                    <Input type="email" placeholder="email@example.com" className="pl-11 h-12 rounded-xl bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100" value={email} onChange={e => setEmail(e.target.value)} required />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="font-bold text-slate-700 dark:text-slate-300">Active WhatsApp</Label>
                                                <div className="relative">
                                                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                                    <Input type="tel" placeholder="+62..." className="pl-11 h-12 rounded-xl bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100" value={whatsapp} onChange={e => setWhatsapp(e.target.value)} required />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* STEP 3: PAYMENT METHOD */}
                            {isBaseVerified && (
                                <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col animate-in slide-in-from-bottom-4">
                                    <div className="p-6 md:p-8 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900">
                                        <div className="flex items-center gap-4">
                                            <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400 font-black text-xl shrink-0">
                                                {role === "presenter" ? "3" : "2"}
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-extrabold text-slate-900 dark:text-white">Payment Method</h3>
                                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Select your preferred channel</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-6 md:p-8 bg-slate-50/30 dark:bg-slate-900/50">
                                        <div className="grid gap-5 md:grid-cols-2">
                                            {isLoadingGateways ? (
                                                <div className="col-span-2 flex justify-center py-4"><Loader2 className="animate-spin w-8 h-8 text-blue-500" /></div>
                                            ) : (
                                                gateways?.map(gw => {
                                                    const isUsd = gw.nama_payment.includes('USD');
                                                    return (
                                                        <PaymentMethodCard
                                                            key={gw.id}
                                                            id={gw.id.toString()}
                                                            name={gw.nama_payment}
                                                            description={`Payment via ${gw.vendor}`}
                                                            logoUrl={`/payment/${gw.vendor.toLowerCase().includes('bni') ? 'bni.png' : 'bank.png'}`} // fallback
                                                            badgeText={isUsd ? "USD" : "IDR"}
                                                            badgeColor={isUsd ? "green" : "orange"}
                                                            selected={selectedMethod === gw.id.toString()}
                                                            onSelect={setSelectedMethod}
                                                        />
                                                    )
                                                })
                                            )}
                                        </div>

                                        {/* ✅ INFORMASI TRANSFER CONDITIONAL (Menyatu & Elegan Tanpa Layer Berlebih) */}
                                        <AnimatePresence>
                                            {selectedMethod && priceData && (() => {
                                                const selectedGateway = gateways?.find(gw => gw.id.toString() === selectedMethod);
                                                const isUsd = selectedGateway?.nama_payment?.includes('USD');
                                                const currentCurrency = isUsd ? 'USD' : 'IDR';

                                                // Logika Admin Fee
                                                const adminFee = isUsd ? 5 : 2500;
                                                const baseAmount = priceData.total_amount;
                                                const totalWithFee = Number(baseAmount) + adminFee;

                                                // Helper format uang
                                                const formatMoney = (val: string | number, curr: string) => {
                                                    return new Intl.NumberFormat(curr === "USD" ? "en-US" : "id-ID", {
                                                        style: "currency",
                                                        currency: curr,
                                                        minimumFractionDigits: 0,
                                                    }).format(Number(val));
                                                };

                                                return (
                                                    <motion.div
                                                        initial={{ opacity: 0, y: 10, height: 0 }}
                                                        animate={{ opacity: 1, y: 0, height: "auto" }}
                                                        exit={{ opacity: 0, height: 0 }}
                                                        className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700"
                                                    >
                                                        {isUsd ? (
                                                            <div className="space-y-5">
                                                                <div className="flex items-center gap-3 text-blue-700 dark:text-blue-400">
                                                                    <Info className="w-5 h-5" />
                                                                    <h4 className="font-bold text-base">International / Non-BNI Transfer (USD)</h4>
                                                                </div>

                                                                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                                                                    If you are paying from an overseas bank or a non-BNI account, please wire your payment to the following bank account details:
                                                                </p>

                                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm bg-white dark:bg-slate-800/50 p-5 rounded-2xl border border-slate-100 dark:border-slate-700">
                                                                    <div>
                                                                        <p className="text-slate-500 dark:text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-1">Account Name</p>
                                                                        <p className="font-bold text-slate-900 dark:text-white">PANITIA PELAKSANA KONASPI XII</p>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-slate-500 dark:text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-1">Account Number</p>
                                                                        <p className="font-bold text-blue-600 dark:text-blue-400 font-mono text-base">2023869027 <span className="text-xs text-slate-500 font-sans">(USD)</span></p>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-slate-500 dark:text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-1">Bank Name</p>
                                                                        <p className="font-bold text-slate-900 dark:text-white">Bank Negara Indonesia (BNI)</p>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-slate-500 dark:text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-1">Swift Code</p>
                                                                        <p className="font-bold text-slate-900 dark:text-white font-mono">BNINIDJASGR</p>
                                                                    </div>
                                                                    <div className="sm:col-span-2">
                                                                        <p className="text-slate-500 dark:text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-1">Bank Address & Branch</p>
                                                                        <p className="font-bold text-slate-900 dark:text-white">Jalan Ngurah Rai 48, Singaraja, Bali, Indonesia (Singaraja Branch)</p>
                                                                    </div>
                                                                </div>

                                                                {/* Peringatan Important Note USD dengan Simulasi Dinamis */}
                                                                <div className="bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/60 dark:border-amber-800/30 rounded-2xl p-5 flex gap-3 shadow-sm">
                                                                    <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                                                                    <div className="text-sm text-amber-900 dark:text-amber-200/90 w-full">
                                                                        <p className="font-bold mb-2">Important Note: Wire Transfer Fees</p>
                                                                        <p className="mb-3">
                                                                            Please ensure the transfer is sent as <strong className="font-extrabold text-amber-950 dark:text-amber-100">"Full Amount" (OUR)</strong>. If not sent as Full Amount, an additional bank fee will be applied.
                                                                        </p>
                                                                        <div className="bg-white/80 dark:bg-slate-900/50 p-4 rounded-xl border border-amber-100 dark:border-amber-900/50">
                                                                            <p className="font-bold text-[10px] text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-widest">Payment Simulation</p>
                                                                            <ul className="space-y-1 font-mono text-sm">
                                                                                <li className="flex justify-between gap-4">
                                                                                    <span>Invoice Amount:</span>
                                                                                    <span>{formatMoney(baseAmount, currentCurrency)}</span>
                                                                                </li>
                                                                                <li className="flex justify-between gap-4 border-b border-amber-100 dark:border-amber-800/50 pb-1.5">
                                                                                    <span>Additional Bank Fee:</span>
                                                                                    <span>{formatMoney(adminFee, currentCurrency)} +</span>
                                                                                </li>
                                                                                <li className="flex justify-between gap-4 font-bold text-blue-700 dark:text-blue-400 pt-1.5">
                                                                                    <span>Total to Transfer:</span>
                                                                                    <span>{formatMoney(totalWithFee, currentCurrency)}</span>
                                                                                </li>
                                                                            </ul>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="space-y-5">
                                                                <div className="flex items-center gap-3 text-emerald-700 dark:text-emerald-400">
                                                                    <Info className="w-5 h-5" />
                                                                    <h4 className="font-bold text-base">Informasi Pembayaran (IDR)</h4>
                                                                </div>

                                                                {/* Peringatan Important Note IDR dengan Simulasi Dinamis */}
                                                                <div className="bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/60 dark:border-amber-800/30 rounded-2xl p-5 flex gap-3 shadow-sm">
                                                                    <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                                                                    <div className="text-sm text-amber-900 dark:text-amber-200/90 w-full">
                                                                        <p className="font-bold mb-2">Important: Non-BNI Transfer / Pembayaran Beda Bank</p>
                                                                        <p className="mb-3">
                                                                            Jika Anda transfer menggunakan bank <strong>selain BNI</strong>, wajib menginput <strong>Nominal Tagihan Pokok + Fee Admin VA ({formatMoney(adminFee, currentCurrency)})</strong> secara manual.
                                                                        </p>
                                                                        <div className="bg-white/80 dark:bg-slate-900/50 p-4 rounded-xl border border-amber-100 dark:border-amber-900/50">
                                                                            <p className="font-bold text-[10px] text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-widest">Contoh Simulasi (Jika Beda Bank):</p>
                                                                            <ul className="space-y-1 font-mono text-sm">
                                                                                <li className="flex justify-between gap-4">
                                                                                    <span>Nominal Tagihan:</span>
                                                                                    <span>{formatMoney(baseAmount, currentCurrency)}</span>
                                                                                </li>
                                                                                <li className="flex justify-between gap-4 border-b border-amber-100 dark:border-amber-800/50 pb-1.5">
                                                                                    <span>Fee Admin VA BNI:</span>
                                                                                    <span>{formatMoney(adminFee, currentCurrency)} +</span>
                                                                                </li>
                                                                                <li className="flex justify-between gap-4 font-bold text-blue-700 dark:text-blue-400 pt-1.5">
                                                                                    <span>Total Diinput:</span>
                                                                                    <span>{formatMoney(totalWithFee, currentCurrency)}</span>
                                                                                </li>
                                                                            </ul>
                                                                        </div>
                                                                        <p className="text-[10px] mt-2 opacity-70 italic leading-snug">
                                                                            * Pembayaran menggunakan sesama bank BNI TIDAK perlu menginput nominal fee tambahan. Biaya transfer antar bank dikenakan terpisah oleh bank pengirim.
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </motion.div>
                                                );
                                            })()}
                                        </AnimatePresence>
                                    </div>
                                </div>
                            )}
                        </form>

                        <div className="block lg:hidden pt-4"><SummaryCard isDesktop={false} /></div>

                        <div className="pt-6 space-y-6">
                            <PaymentGuide />
                            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xl border border-slate-200 dark:border-slate-800">
                                <div className="flex items-center gap-5">
                                    <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-2xl text-slate-700 dark:text-slate-300 shrink-0"><LifeBuoy className="w-7 h-7" /></div>
                                    <div className="text-left">
                                        <h4 className="font-extrabold text-slate-800 dark:text-slate-200 text-lg">Help Center</h4>
                                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Contact the committee if you experience issues.</p>
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
                                    <a href="mailto:konaspixii@undiksha.ac.id" className="flex items-center justify-center gap-2 px-6 py-3 w-full sm:w-auto bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 shadow-sm"><Mail className="w-4 h-4" /> Email</a>
                                    <a href="https://wa.me/6287730060048" className="flex items-center justify-center gap-2 px-6 py-3 w-full sm:w-auto bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-xl text-sm font-bold text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40 shadow-sm"><Phone className="w-4 h-4" /> WhatsApp</a>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* --- RIGHT COLUMN --- */}
                    <aside className="w-full lg:col-span-4 lg:sticky lg:top-28 space-y-6">
                        <div className="hidden lg:block"><SummaryCard isDesktop={true} /></div>
                        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-xl overflow-hidden hidden md:block">
                            <div className="p-6 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900">
                                <h4 className="font-extrabold text-slate-800 dark:text-white flex items-center gap-2 text-sm uppercase tracking-wider">
                                    <ListChecks className="w-5 h-5 text-blue-600 dark:text-blue-500" /> Registration Flow
                                </h4>
                            </div>
                            <div className="p-6 md:p-8 bg-slate-50/30 dark:bg-slate-900/50">
                                <div className="relative border-l-2 border-slate-200 dark:border-slate-700 ml-3 space-y-7 pb-2">
                                    {role === "presenter" ? (
                                        <>
                                            <div className="relative pl-8">
                                                <div className="absolute -left-[14px] top-0 w-7 h-7 rounded-full bg-white dark:bg-slate-800 border-2 border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 flex items-center justify-center text-xs font-extrabold shadow-sm">1</div>
                                                <h5 className="font-bold text-slate-800 dark:text-slate-200 text-sm">Conference Validation</h5>
                                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed">Pull paper data via Submission ID.</p>
                                            </div>
                                            <div className="relative pl-8">
                                                <div className="absolute -left-[14px] top-0 w-7 h-7 rounded-full bg-white dark:bg-slate-800 border-2 border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 flex items-center justify-center text-xs font-extrabold shadow-sm">2</div>
                                                <h5 className="font-bold text-slate-800 dark:text-slate-200 text-sm">Complete Data</h5>
                                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed">Fill participant & affiliation form.</p>
                                            </div>
                                            <div className="relative pl-8">
                                                <div className="absolute -left-[14px] top-0 w-7 h-7 rounded-full bg-white dark:bg-slate-800 border-2 border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 flex items-center justify-center text-xs font-extrabold shadow-sm">3</div>
                                                <h5 className="font-bold text-slate-800 dark:text-slate-200 text-sm">Review (If Applicable)</h5>
                                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed">External students require ID validation.</p>
                                            </div>
                                            <div className="relative pl-8">
                                                <div className="absolute -left-[14px] top-0 w-7 h-7 rounded-full bg-white dark:bg-slate-800 border-2 border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 flex items-center justify-center text-xs font-extrabold shadow-sm">4</div>
                                                <h5 className="font-bold text-slate-800 dark:text-slate-200 text-sm">Generate VA</h5>
                                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed">Invoice sent to registered Email/WA.</p>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="relative pl-8">
                                                <div className="absolute -left-[14px] top-0 w-7 h-7 rounded-full bg-white dark:bg-slate-800 border-2 border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 flex items-center justify-center text-xs font-extrabold shadow-sm">1</div>
                                                <h5 className="font-bold text-slate-800 dark:text-slate-200 text-sm">Complete Data</h5>
                                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed">Select track & fill participant info.</p>
                                            </div>
                                            <div className="relative pl-8">
                                                <div className="absolute -left-[14px] top-0 w-7 h-7 rounded-full bg-white dark:bg-slate-800 border-2 border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 flex items-center justify-center text-xs font-extrabold shadow-sm">2</div>
                                                <h5 className="font-bold text-slate-800 dark:text-slate-200 text-sm">Review (If Applicable)</h5>
                                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed">External students require ID validation.</p>
                                            </div>
                                            <div className="relative pl-8">
                                                <div className="absolute -left-[14px] top-0 w-7 h-7 rounded-full bg-white dark:bg-slate-800 border-2 border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 flex items-center justify-center text-xs font-extrabold shadow-sm">3</div>
                                                <h5 className="font-bold text-slate-800 dark:text-slate-200 text-sm">Generate VA</h5>
                                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed">Invoice sent to registered Email/WA.</p>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>
            </section>

            {/* --- MOBILE FLOATING ACTION BAR --- */}
            <AnimatePresence>
                {isBaseVerified && (
                    <motion.div initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }} className="lg:hidden fixed bottom-0 left-0 right-0 p-4 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-slate-200 dark:border-slate-800 z-50 shadow-[0_-10px_30px_-10px_rgba(0,0,0,0.15)]">
                        <ActionButton />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}